import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { sitePack } from './sitePacks';
import { SITE_LANGS } from './meta';

const LanguageContext = createContext({
  lang: 'en',
  setLang: () => {},
  t: sitePack('en'),
  langs: SITE_LANGS,
});

function readStoredLang() {
  try {
    const raw = localStorage.getItem('vani:lang');
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    return typeof parsed === 'string' ? parsed : raw;
  } catch (_) {
    try { return localStorage.getItem('vani:lang'); } catch { return null; }
  }
}

export function LanguageProvider({ children }) {
  const [lang, setLangState] = useState(() => {
    if (typeof window === 'undefined') return 'en';
    const stored = readStoredLang();
    if (stored && SITE_LANGS.includes(stored)) return stored;
    const guess = (navigator.language || 'en').split('-')[0];
    return SITE_LANGS.includes(guess) ? guess : 'en';
  });

  const setLang = useCallback((code) => {
    const next = SITE_LANGS.includes(String(code || 'en').split('-')[0])
      ? String(code).split('-')[0]
      : 'en';
    setLangState(next);
    try { localStorage.setItem('vani:lang', JSON.stringify(next)); } catch (_) {}
    if (typeof document !== 'undefined') document.documentElement.lang = next;
  }, []);

  useEffect(() => {
    if (typeof document !== 'undefined') document.documentElement.lang = lang;
  }, [lang]);

  const value = useMemo(
    () => ({ lang, setLang, t: sitePack(lang), langs: SITE_LANGS }),
    [lang, setLang]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  return useContext(LanguageContext);
}
